import React, { useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  TextField,
  Autocomplete,
  Snackbar,
  Alert,
  Box,
  Button as MuiCButton,
} from '@mui/material';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { Form, Formik } from 'formik';
import { AddBox } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { Storage } from 'aws-amplify';
import { AddEventSchema } from './AddEventSchema';
import {
  FeedbackError,
  Input,
  Textarea,
  Button,
  CustomDatePicker,
  CustomTimePicker,
  ChipList,
  Dropdown,
  TranslucentContainer,
  AlertComponent,
  RecurrentEventModal,
  DropzoneFile,
} from '../../../../../components';
import * as LocationActions from '../../../../../core/location/action';
import * as EventActions from '../../../../../core/events/action';
import ConstSizeList from '../components/CostSizeList/ConstSizeList';
import { VENUE_STATUS } from '../../../../../constants/venue-status';
import { onDisplayAlert } from '../../../../../utils/alerts/alerts';
import { APP_ROUTES } from '../../../../../constants/app-routes';
import EventSurchargeList from '../components/EventSurcharges/EventSurchargeList';
import EventAlert from './EventAlert';
import { useIsMobile } from '../../../../../hooks/useIsMobile';
import * as VenuesActions from '../../../../../core/venues/action';

function FormContentEvent({ onSaveEvent }) {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const {
    venues: { currentVenue, venuePictures, eventPicture, currentVenuePictures },
    users: { user },
    location: { states, cities, loadingStates, loadingCities },
  } = useSelector((store) => store);

  // Sanitize phone number to remove non-numeric characters
  const sanitizePhoneNumber = (phone) => {
    if (!phone) return '';
    // Remove all non-numeric characters (including +, spaces, dashes, parentheses, etc.)
    return phone.replace(/\D/g, '');
  };

  // Memoize initialValues to prevent unnecessary re-renders and form resets
  const initialValues = useMemo(
    () => ({
      name: '',
      event_date: null,
      cancellation_deadline: '',
      refund_deadline: '',
      hour_refund_deadline: '',
      description: '',
      street_address: '',
      digital_marketing: false,
      commercial_insurance: false,
      unlisted: false,
      hour_start_event: '',
      hour_end_event: '',
      application_deadline: '',
      max_applications: '',
      hour_vendor_setup_start: '',
      hour_vendor_take_down_start: '',
      hour_vendor_take_down_end: '',
      equipment_required: '',
      isRecurring: false,
      allow_upfront_payment: false,
      fk_state: undefined,
      fk_city: undefined,
      contact_name: user?.name || '',
      contact_email: user?.email || '',
      contact_phone_number: sanitizePhoneNumber(user?.phone_number) || '',
      space_type: '',
    }),
    [user?.name, user?.email, user?.phone_number],
  );

  const repeatEveryList = [
    { id: 'no_repeat', name: 'No Repeat' },
    { id: 'day', name: 'Day' },
    { id: 'week', name: 'Week' },
    { id: 'month', name: 'Month' },
    { id: 'year', name: 'Year' },
  ];
  const [picturesList, setPicturesList] = useState([]);
  const [recurrentDates, setRecurrentDates] = useState([]);
  const [newEquipment, setNewEquipment] = useState('');
  const [endRecurrenceEventDate, setEndRecurrenceEventDate] = useState(moment());
  const [Hints, setHints] = useState('');
  const [repeatEvery, setRepeatEveryString] = useState('no_repeat');
  const [openRecurrentEventModal, setOpenRecurrentEventModal] = useState(false);
  const [chipEquipmentList, setChipEquipmentList] = useState([]);
  const [eventCostSizeList, setEventCostSizeList] = useState([]);
  const [eventSurchargeList, setEventSurchargeList] = useState([]);
  const [marketingContUpload, setMarketingContUpload] = useState();
  const [snackMessage, setSnackMessage] = useState();
  const [openSnack, setOpenSnack] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [buffer, setBuffer] = useState(0);
  const marketingData = useRef(null);

  const [imageUrl, setImageUrl] = useState();
  const justifyContentValue = isMobile ? 'start' : 'center';
  const [newEventCostSize, setNewEventCostSize] = useState({
    industry: '',
    cost: '',
    size: '',
    space_number: '',
  });
  const [newEventSurcharge, setNewEventSurcharge] = useState({
    name: '',
    price: '',
  });
  const [recurrentConfig, setRecurrentConfig] = useState({
    repeat_every: '',
    repeat_every_time: '',
    recurring_end_date: '',
  });

  useEffect(() => {
    if (currentVenuePictures?.length > 0) setPicturesList([...currentVenuePictures]);
    dispatch(LocationActions.getStates());
  }, [dispatch, currentVenuePictures]);
  const onChangeFiles = (incomingFiles) => {
    const image = incomingFiles.filter((file) => file.valid);
    dispatch(VenuesActions.addImageToEvent(image));
  };
  const handleDeleteImage = async ({ key }) => {
    dispatch(VenuesActions.deleteVenuePicture(key));
  };
  const handleLoadMarketingDocs = (e) => {
    if (!e.target.files) {
      return;
    }
    const file = e.target.files[0];
    // 30 MB === 31457280  Bytes
    if (file.size >= 31457280) {
      setSnackMessage('Digital Marketing exceeds the allowed size (30MB)');
      setOpenSnack(true);
      setMarketingContUpload(false);
      e.target.value = '';
    } else {
      setMarketingContUpload(file);
    }
  };

  const handleUploadMarketingDocs = async () => {
    setUploading(true);

    if (marketingContUpload) {
      try {
        const fileName = `mrktng_docs_${uuidv4()}_${marketingContUpload.name}`;
        const filePath = `VENUES/${user.id}/EVENTS/${fileName}`;
        const resultUpload = await Storage.put(filePath, marketingContUpload, {
          metadata: { name: `${marketingContUpload.name}` },
          level: 'public',
        });
        if (resultUpload.key !== '') {
          setMarketingContUpload(false);
          marketingData.current = [
            {
              type: 'event',
              name: fileName,
              key: resultUpload.key,
            },
          ];
          setTimeout(async () => {
            const getElement = await Storage.get(resultUpload.key);
            setImageUrl(getElement);
          }, 2000);
        } else {
          setSnackMessage('Error uploading Marketing Docs, please try again!');
          setOpenSnack(true);
        }
      } catch (error) {
        console.log('Error uploading frontFile: ', error);
      }
    }
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnack(false);
    setSnackMessage('');
  };

  const setSelectedState = (stateSelected, setFieldValueFunc) => {
    if (stateSelected && stateSelected.id) {
      dispatch(LocationActions.getCitiesByStateID(stateSelected.id));
      // Clear city selection when state changes
      if (setFieldValueFunc) {
        setFieldValueFunc('fk_city', undefined);
      }
    }
  };

  const onSubmit = async (values, ev) => {
    const { setSubmitting } = ev;
    try {
      if (eventCostSizeList?.length <= 0) {
        onDisplayAlert({
          title: 'Event Space Size & Cost',
          html: (
            <p>
              Is required to add at least one space <b>dimension and cost</b> for your event.
            </p>
          ),
          type: 'info',
        });
        return;
      }

      if (values.digital_marketing) {
        if (!marketingContUpload) {
          onDisplayAlert({
            title: 'Digital Marketing',
            html: <p>Has not been uploaded, please load a file.</p>,
            type: 'info',
          });
          return;
        }
      }
      if (eventPicture?.length <= 0) {
        onDisplayAlert({
          title: 'Picture',
          html: <p>Is required to add an image</p>,
          type: 'info',
        });
        return;
      }
      dispatch(EventActions.onChangeLoading(true));
      const isRecurrent = repeatEvery && repeatEvery !== 'no_repeat';
      if (values.digital_marketing) await handleUploadMarketingDocs();
      const result = {
        event: {
          name: values.name,
          photo: currentVenue?.banner,
          description: values.description,
          street_address: values.street_address,
          fk_venue: currentVenue.id,
          allow_upfront_payment: values.allow_upfront_payment,
          unlisted: values.unlisted,
          fk_city: values.fk_city,
          fk_state: values.fk_state,
          contact_name: values.contact_name,
          contact_email: values.contact_email,
          contact_phone_number: values.contact_phone_number,
          space_type: values.space_type,
          photoEvent: eventPicture,
          venue: currentVenue,
        },
        isRecurrent,
        recurrentDates: !isRecurrent ? undefined : recurrentDates,
        eventSurcharges: eventSurchargeList.map((i) => ({ name: i.name, price: i.price })),
        equipment_required: chipEquipmentList.map((e) => e.label),
        cost_size_event: eventCostSizeList.map((i) => ({
          industry: i.industry,
          size: i.size,
          cost: i.cost,
          space_number: i.space_number,
        })),
        parameters_event: {
          event_date: values.event_date.format('YYYY-MM-DD'),
          cancellation_deadline: values.cancellation_deadline.format('YYYY-MM-DD'),
          refund_deadline: values.refund_deadline.format('YYYY-MM-DD'),
          hour_refund_deadline: values.hour_refund_deadline,
          hour_start_event: values.hour_start_event,
          hour_end_event: values.hour_end_event,
          hour_vendor_setup_start: values.hour_vendor_setup_start,
          hour_vendor_take_down_start: values.hour_vendor_take_down_start,
          hour_vendor_take_down_end: values.hour_vendor_take_down_end,
          digital_marketing: values.digital_marketing,
          commercial_insurance: values.commercial_insurance,
          max_applications: values.max_applications,
          application_deadline: values.application_deadline.format('YYYY-MM-DD'),
        },
        marketingData: marketingData.current ? marketingData.current : null,
      };
      if (isRecurrent) result.parameters_event = { ...result.parameters_event, ...recurrentConfig };
      await onSaveEvent(result);
    } catch (err) {
      console.log(err);
    } finally {
      setSubmitting(false);
    }
  };
  const onCloseRecurrentEventModal = () => setOpenRecurrentEventModal(false);
  const onOpenRecurrentEventModal = () => setOpenRecurrentEventModal(true);

  const onChangeRepeatEverySelect = (e) => {
    const { value } = e.target;
    setRepeatEveryString(e.target.value);
    if (value === repeatEvery) {
      onOpenRecurrentEventModal();
    }
    if (value === 'no_repeat') setRecurrentDates([]);
    if (value !== 'no_repeat') onOpenRecurrentEventModal();
  };

  const onDeleteEquipment = (chipToDelete) => () => {
    setChipEquipmentList((chips) => chips.filter((chip) => chip.key !== chipToDelete.key));
  };

  const onDeleteSizeCost = (itemToDelete) => () => {
    setEventCostSizeList((item) => item.filter((chip) => chip.key !== itemToDelete.key));
  };

  const onDeleteSurcharge = (itemToDelete) => () => {
    setEventSurchargeList((item) => item.filter((chip) => chip.key !== itemToDelete.key));
  };

  const addNewConstSizeEvent = () => {
    // eslint-disable-next-line camelcase
    const { industry, cost, size, space_number } = newEventCostSize;
    if (
      industry.toString().trim() !== '' &&
      cost.toString().trim() !== '' &&
      size.trim() !== '' &&
      // eslint-disable-next-line camelcase
      space_number.trim() !== ''
    ) {
      const item = { key: uuidv4(), ...newEventCostSize };
      setEventCostSizeList([...eventCostSizeList, item]);
      setNewEventCostSize({
        industry: '',
        cost: '',
        size: '',
        space_number: '',
      });
    }
  };

  const addNewSurchargeEvent = () => {
    const { name, price } = newEventSurcharge;
    console.log({ newEventSurcharge });
    if (name.toString().trim() !== '' && price.trim() !== '') {
      const item = { key: uuidv4(), ...newEventSurcharge };
      setEventSurchargeList([...eventSurchargeList, item]);
      setNewEventSurcharge({
        name: '',
        price: '',
      });
    }
  };

  const handleChangeSurcharges = (e) => {
    setNewEventSurcharge({ ...newEventSurcharge, [e.target.name]: e.target.value });
  };

  const handleChangeCostSize = (e) => {
    setNewEventCostSize({ ...newEventCostSize, [e.target.name]: e.target.value });
  };

  const onAddEquipment = (e) => {
    const key = uuidv4();
    if (e.key === 'Enter' && newEquipment.trim() !== '') {
      e.preventDefault();
      setChipEquipmentList([...chipEquipmentList, { key, label: newEquipment }]);
      setNewEquipment('');
    }
  };

  const onSaveRecurrentEventData = (data) => {
    console.log({ data });
    if (data) {
      const { Hints: HintsComponent, endRecurrence, repeatEveryCount, eventRecurrentList } = data;
      setHints(HintsComponent);
      setRecurrentDates(eventRecurrentList);
      setEndRecurrenceEventDate(endRecurrence);

      const recurrentConf = {
        repeat_every: repeatEvery,
        repeat_every_time: repeatEveryCount,
        recurring_end_date: endRecurrence,
      };
      setRecurrentConfig({ ...recurrentConf });
      console.log(recurrentConf);
      setOpenRecurrentEventModal(false);
    }
  };
  const canCreateEvents = () =>
    currentVenue !== null && currentVenue?.status === VENUE_STATUS.APPROVED;
  // user?.stripe_details !== null &&
  // user?.stripe_details?.details_submitted;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={AddEventSchema}
      onSubmit={onSubmit}
      enableReinitialize>
      {({
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isSubmitting,
      }) => (
        <Form
          onSubmit={handleSubmit}
          noValidate
          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}>
          <Grid container paddingX={isMobile ? 0 : '10%'}>
            {!canCreateEvents() && (
              <EventAlert isApproved={currentVenue?.status === VENUE_STATUS.APPROVED} />
            )}
            <Grid container justifyContent="space-between" marginTop={5} spacing={1}>
              <Typography textAlign="center" variant="h6" fontWeight="bold">
                Picture
              </Typography>
            </Grid>
            <Grid container justifyContent="space-between" marginTop={5} spacing={1}>
              <Grid item xs={12} sm={12} md={11.5}>
                <DropzoneFile
                  files={eventPicture}
                  onChangeFiles={onChangeFiles}
                  maxFile={1}
                  required
                />
              </Grid>
            </Grid>
            Max. File 1.
            <Grid container justifyContent="space-between" marginTop={5} spacing={1}>
              <Grid item xs={12} sm={7}>
                <Input
                  placeholder="Event Name"
                  name="name"
                  type="text"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.name && touched.name && 'error'}
                />
                <FeedbackError name="name" errors={errors} touched={touched} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <CustomDatePicker
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  name="event_date"
                  placeholder="Event Date"
                />
                <FeedbackError name="event_date" errors={errors} touched={touched} />
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-start" marginTop={1}>
              <Grid item xs={12} sm={12} md={11.5}>
                <Input
                  placeholder="Street address"
                  name="street_address"
                  type="text"
                  value={values.street_address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.street_address && touched.street_address && 'error'}
                />
                <FeedbackError name="street_address" errors={errors} touched={touched} />
              </Grid>
            </Grid>
            <Grid container justifyContent="space-between" marginTop={1} spacing={1}>
              <Grid item xs={12} sm={5}>
                <Autocomplete
                  id="fk_state"
                  name="fk_state"
                  classes={{ label: 'my-class-name' }}
                  placeholder="State"
                  options={states || []}
                  disablePortal
                  loading={loadingStates}
                  disabled={loadingStates}
                  onChange={(e, value) => {
                    if (value) {
                      setFieldValue('fk_state', value.id);
                      setSelectedState(value, setFieldValue);
                    } else {
                      setFieldValue('fk_state', undefined);
                      setFieldValue('fk_city', undefined);
                    }
                  }}
                  getOptionLabel={(option) => `${option.state_code} - ${option.name}`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State"
                      placeholder={loadingStates ? 'Loading states...' : 'Select a state'}
                    />
                  )}
                  noOptionsText={loadingStates ? 'Loading...' : 'No states available'}
                  clearOnEscape
                  className={errors.fk_state && touched.fk_state && 'error'}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '50px',
                      background: '#ffffff',
                      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                      height: '50px',
                    },
                  }}
                />
                <FeedbackError name="fk_state" errors={errors} touched={touched} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  id="fk_city"
                  name="fk_city"
                  disableClearable
                  options={cities || []}
                  getOptionLabel={(option) => option.name || ''}
                  disablePortal
                  loading={loadingCities}
                  disabled={!values.fk_state || loadingCities}
                  renderInput={(params) => {
                    let placeholder = 'Select a city';
                    if (!values.fk_state) {
                      placeholder = 'Select a state first';
                    } else if (loadingCities) {
                      placeholder = 'Loading cities...';
                    }
                    return <TextField {...params} label="City" placeholder={placeholder} />;
                  }}
                  noOptionsText={(() => {
                    if (!values.fk_state) return 'Please select a state first';
                    if (loadingCities) return 'Loading...';
                    return 'No cities available';
                  })()}
                  onChange={(e, value) => {
                    if (value) {
                      setFieldValue('fk_city', value.id);
                    } else {
                      setFieldValue('fk_city', undefined);
                    }
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '50px',
                      background: '#ffffff',
                      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                      height: '50px',
                      '&.Mui-disabled': {
                        backgroundColor: '#f5f5f5',
                      },
                    },
                  }}
                />
                <FeedbackError name="fk_city" errors={errors} touched={touched} />
              </Grid>
            </Grid>
            <Grid container marginTop={1} justifyContent="space-between" spacing={1}>
              <Grid item xs={12} sm={3.5} md={4}>
                <FormControl
                  fullWidth
                  error={errors.space_type && touched.space_type}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '50px',
                      background: '#ffffff',
                      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                      height: '50px',
                    },
                  }}>
                  <InputLabel id="space-type-label">Event Space Type</InputLabel>
                  <Select
                    labelId="space-type-label"
                    id="space_type"
                    name="space_type"
                    value={values.space_type}
                    label="Event Space Type"
                    onChange={handleChange}
                    onBlur={handleBlur}>
                    <MenuItem value="both">Both</MenuItem>
                    <MenuItem value="indoor">Indoor</MenuItem>
                    <MenuItem value="outdoor">Outdoor</MenuItem>
                  </Select>
                </FormControl>
                <FeedbackError name="space_type" errors={errors} touched={touched} />
              </Grid>
              <Grid item xs={12} sm={3.5} md={4}>
                <CustomDatePicker
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  name="cancellation_deadline"
                  onBlur={handleBlur}
                  placeholder="Cancellation Deadline"
                />
                <FeedbackError name="cancellation_deadline" errors={errors} touched={touched} />
              </Grid>
            </Grid>
            <Grid container marginTop={1} justifyContent="space-between" spacing={1}>
              <Grid item xs={12} sm={3.5} md={4}>
                <Input
                  placeholder="How many vendor applications you’re willing to review?"
                  name="max_applications"
                  type="number"
                  value={values.max_applications}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ width: '82%' }}
                  className={errors.max_applications && touched.max_applications && 'error'}
                />
                <FeedbackError name="max_applications" errors={errors} touched={touched} />
              </Grid>
              <Grid item xs={12} sm={3.5} md={4}>
                <CustomDatePicker
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  name="application_deadline"
                  onBlur={handleBlur}
                  placeholder="Applications deadline"
                />
                <FeedbackError name="application_deadline" errors={errors} touched={touched} />
              </Grid>
            </Grid>
            <Grid container marginTop={1} justifyContent="space-between" spacing={1}>
              <Grid item xs={12} sm={3.5} md={4}>
                <CustomDatePicker
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  name="refund_deadline"
                  onBlur={handleBlur}
                  placeholder="Refund Deadline"
                />
                <FeedbackError name="refund_deadline" errors={errors} touched={touched} />
              </Grid>
              <Grid item xs={12} sm={5} md={5}>
                <CustomTimePicker
                  name="hour_refund_deadline"
                  label="Refund end time"
                  value={values.hour_refund_deadline}
                  setFieldValue={setFieldValue}
                  className={errors.hour_refund_deadline && touched.hour_refund_deadline && 'error'}
                />
                <FeedbackError name="hour_refund_deadline" errors={errors} touched={touched} />
              </Grid>
            </Grid>
            <Grid container marginTop={1} justifyContent="space-between" spacing={1}>
              <Grid item xs={12} sm={5} md={5}>
                <CustomTimePicker
                  name="hour_start_event"
                  label="Event start time"
                  value={values.hour_start_event}
                  setFieldValue={setFieldValue}
                  className={errors.hour_start_event && touched.hour_start_event && 'error'}
                />
                <FeedbackError name="hour_start_event" errors={errors} touched={touched} />
              </Grid>
              <Grid item xs={12} sm={5} md={5}>
                <CustomTimePicker
                  name="hour_end_event"
                  label="Event end time"
                  value={values.hour_end_event}
                  setFieldValue={setFieldValue}
                  className={errors.hour_end_event && touched.hour_end_event && 'error'}
                />
                <FeedbackError name="hour_end_event" errors={errors} touched={touched} />
              </Grid>
            </Grid>
            <Grid container marginTop={1} justifyContent="start" spacing={1}>
              <Grid item xs={12} sm={4}>
                <InputLabel id="demo-select-small">Repeat every</InputLabel>
                <FormControl sx={{ m: 1, minWidth: 150, width: '100%' }} size="small">
                  <Select
                    autoWidth
                    value={repeatEvery}
                    labelId="demo-simple-select-autowidth-label"
                    id="demo-simple-select-autowidth"
                    onChange={onChangeRepeatEverySelect}>
                    {repeatEveryList.map((i) => (
                      <MenuItem key={i.id} value={i.id}>
                        {i.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {Hints && repeatEvery !== 'no_repeat' && recurrentDates?.length > 0 && (
                <Grid item sm={12} paddingLeft={14}>
                  <Typography color="teal" variant="body1">
                    {Hints} {moment(endRecurrenceEventDate).format('LL')}.
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Grid container marginTop={1} justifyContent="start" spacing={1}>
              <Grid item sm={12} paddingLeft={13}>
                <div style={{ display: 'block', justifyContent: 'start' }}>
                  <p style={{ marginTop: 10 }}> Digital marketing is provided by the venue</p>
                  <label>Yes</label>
                  <Checkbox
                    name="yes"
                    checked={values.digital_marketing}
                    onChange={(e) => {
                      if (e.target.name === 'yes') {
                        setFieldValue('digital_marketing', true);
                      } else if (e.target.name === 'no') {
                        setFieldValue('digital_marketing', false);
                      }
                    }}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                  <label>No</label>
                  <Checkbox
                    name="no"
                    checked={!values.digital_marketing}
                    onChange={(e) => {
                      setMarketingContUpload(false);
                      if (e.target.name === 'yes') {
                        setFieldValue('digital_marketing', true);
                      } else if (e.target.name === 'no') {
                        setFieldValue('digital_marketing', false);
                      }
                    }}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                </div>
              </Grid>
              {values.digital_marketing && (
                <>
                  <Grid item sm={12} paddingLeft={13}>
                    <AlertComponent
                      title="Note"
                      content={
                        <>
                          Upload here your digital marketing files, whether more than one document
                          needs to be updated compressed in a .zip file, and upload it. Max limit{' '}
                          <b>30MB</b>.
                        </>
                      }
                      severity="warning"
                    />
                  </Grid>
                  <Grid item sm={12} paddingLeft={13}>
                    <input
                      type="file"
                      id="asset-photo"
                      name="asset-photo-upload"
                      accept=""
                      onChange={handleLoadMarketingDocs}
                    />
                    {/* {marketingContUpload && (
                      <MuiCButton
                        variant="outlined"
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={() => handleUploadMarketingDocs()}>
                        Upload
                      </MuiCButton>
                    )} */}
                  </Grid>
                </>
              )}
            </Grid>
            <Grid container marginTop={1} justifyContent="start" spacing={1}>
              <Grid item sm={6} paddingLeft={13}>
                <div style={{ display: 'block', justifyContent: 'start' }}>
                  <p style={{ marginTop: 10 }}> Need to have business insurance</p>
                  <label>Yes</label>
                  <Checkbox
                    name="yes"
                    checked={values.commercial_insurance}
                    onChange={(e) => {
                      if (e.target.name === 'yes') {
                        setFieldValue('commercial_insurance', true);
                      } else if (e.target.name === 'no') {
                        setFieldValue('commercial_insurance', false);
                      }
                    }}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                  <label>No</label>
                  <Checkbox
                    name="no"
                    checked={!values.commercial_insurance}
                    onChange={(e) => {
                      if (e.target.name === 'yes') {
                        setFieldValue('commercial_insurance', true);
                      } else if (e.target.name === 'no') {
                        setFieldValue('commercial_insurance', false);
                      }
                    }}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                </div>
              </Grid>
              <Grid item sm={12} paddingLeft={13}>
                <div style={{ display: 'block', justifyContent: 'start' }}>
                  <label>Allow 50% upfront payment?</label>
                  <Checkbox
                    name="allow_upfront_payment"
                    checked={values.allow_upfront_payment}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                </div>
              </Grid>
              {values.allow_upfront_payment && (
                <Grid item sm={12} paddingLeft={13}>
                  <AlertComponent
                    title="Upfront Payment"
                    content={
                      <>
                        If you select this option, you must invoice the approved vendors through{' '}
                        <b>Stripe</b> prior to the event.
                      </>
                    }
                    severity="warning"
                  />
                </Grid>
              )}
            </Grid>
            <Grid container justifyContent="center" marginTop={1}>
              <Grid item xs={12} sm={12}>
                <Textarea
                  type="text"
                  name="description"
                  placeholder="Tell us more about your event..."
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.description && touched.description && 'error'}
                />
                <FeedbackError name="description" errors={errors} touched={touched} />
              </Grid>
            </Grid>
            <Grid container justifyContent="start" marginTop={1} xs={12} sm={12}>
              <Dropdown hidden={false} title="Event Setup and Take Down">
                <Grid container justifyContent="space-between">
                  <Grid item xs={12} sm={4} md={3}>
                    <CustomTimePicker
                      name="hour_vendor_setup_start"
                      label="Vendor setup begins"
                      value={values.hour_vendor_setup_start}
                      setFieldValue={setFieldValue}
                      className={
                        errors.hour_vendor_setup_start && touched.hour_vendor_setup_start && 'error'
                      }
                    />
                    <FeedbackError
                      name="hour_vendor_setup_start"
                      errors={errors}
                      touched={touched}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={3}>
                    <CustomTimePicker
                      name="hour_vendor_take_down_start"
                      label="Vendor take down start"
                      value={values.hour_vendor_take_down_start}
                      setFieldValue={setFieldValue}
                      className={
                        errors.hour_vendor_take_down_start &&
                        touched.hour_vendor_take_down_start &&
                        'error'
                      }
                    />
                    <FeedbackError
                      name="hour_vendor_take_down_start"
                      errors={errors}
                      touched={touched}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={3}>
                    <CustomTimePicker
                      name="hour_vendor_take_down_end"
                      label="Vendor take down end"
                      value={values.hour_vendor_take_down_end}
                      setFieldValue={setFieldValue}
                      className={
                        errors.hour_vendor_take_down_end &&
                        touched.hour_vendor_take_down_end &&
                        'error'
                      }
                    />
                    <FeedbackError
                      name="hour_vendor_take_down_end"
                      errors={errors}
                      touched={touched}
                    />
                  </Grid>
                </Grid>
              </Dropdown>
            </Grid>
            <Grid container justifyContent="start" marginTop={1} xs={12} sm={12}>
              <Dropdown hidden={false} title="Point of Contact">
                <Grid container justifyContent="space-evenly">
                  <Grid item xs={12} sm={4}>
                    <Input
                      name="contact_name"
                      type="text"
                      placeholder="Contact name"
                      value={values.contact_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={errors.name && touched.name && 'error'}
                    />
                    <FeedbackError name="contact_name" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Input
                      name="contact_phone_number"
                      type="text"
                      placeholder="Contact phone number"
                      value={values.contact_phone_number}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={errors.name && touched.name && 'error'}
                    />
                    <FeedbackError name="contact_phone_number" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={8}>
                    <Input
                      name="contact_email"
                      type="text"
                      placeholder="Contact email"
                      value={values.contact_email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={errors.name && touched.name && 'error'}
                    />
                    <FeedbackError name="contact_email" errors={errors} touched={touched} />
                  </Grid>
                </Grid>
              </Dropdown>
            </Grid>
            <Grid container justifyContent="start" marginTop={1} xs={12} sm={12}>
              <Dropdown hidden={false} title="Equipment required to attend (optional)">
                <Grid container justifyContent="start">
                  <Grid item xs={12} sm={7}>
                    <Input
                      type="text"
                      placeholder="Type a material needed to attend and press enter"
                      value={newEquipment}
                      onChange={(e) => setNewEquipment(e.target.value)}
                      onKeyDown={onAddEquipment}
                    />
                  </Grid>
                  <Grid item xs={12} sm={10}>
                    <ChipList chipList={chipEquipmentList} onDelete={onDeleteEquipment} />
                  </Grid>
                </Grid>
              </Dropdown>
            </Grid>
            <Grid container justifyContent="start" marginTop={1} xs={12} sm={12}>
              <Dropdown hidden={false} title="Event Surcharges (optional)">
                <Grid container justifyContent="space-between" marginTop={2}>
                  <Grid item xs={12} sm={3}>
                    <label style={{ color: 'gray', marginLeft: 5 }}> Amenity Name </label>
                    <Input
                      name="name"
                      type="text"
                      value={newEventSurcharge.name}
                      placeholder="Name"
                      onChange={handleChangeSurcharges}
                    />
                  </Grid>
                  <Grid item xs={8} sm={2}>
                    <label style={{ color: 'gray', marginLeft: 5 }}>Price </label>
                    <Input
                      name="price"
                      type="number"
                      value={newEventSurcharge.price}
                      placeholder="Price"
                      onChange={handleChangeSurcharges}
                    />
                  </Grid>
                  <Grid item xs={1} sm={1} marginLeft={5} marginTop={3}>
                    <IconButton
                      sx={{ fontSize: '80px' }}
                      color="primary"
                      component="label"
                      size="large"
                      onClick={() => addNewSurchargeEvent()}>
                      <AddBox />
                    </IconButton>
                  </Grid>
                  <Grid item sm={1} />
                </Grid>
                {eventSurchargeList.length > 0 && (
                  <Grid item justifyContent="center">
                    <Grid xs={12} sm={11}>
                      <EventSurchargeList items={eventSurchargeList} onDelete={onDeleteSurcharge} />
                    </Grid>
                  </Grid>
                )}
              </Dropdown>
            </Grid>
            <Grid container justifyContent="start" marginTop={1}>
              <Dropdown hidden={false} title="Event Space Size & Cost (required)">
                <Grid container justifyContent="space-between" marginTop={2}>
                  <Grid item xs={12} sm={12} md={1.5}>
                    <label> Vendor Industry </label>
                    <Input
                      name="industry"
                      type="text"
                      value={newEventCostSize.industry}
                      placeholder="ie: jewelry, art, etc."
                      onChange={handleChangeCostSize}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={1.5}>
                    <label> Space Size (in feet) </label>
                    <Input
                      name="size"
                      type="text"
                      value={newEventCostSize.size}
                      placeholder="Space size"
                      onChange={handleChangeCostSize}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={1.5}>
                    <label> Number of Spaces </label>
                    <Input
                      name="space_number"
                      type="number"
                      value={newEventCostSize.space_number}
                      placeholder="# of Spaces"
                      onChange={handleChangeCostSize}
                    />
                  </Grid>
                  <Grid item xs={9} sm={9} md={1.5}>
                    <label> Space Cost </label>
                    <div>
                      <Input
                        name="cost"
                        type="number"
                        value={newEventCostSize.cost}
                        placeholder="$ cost"
                        onChange={handleChangeCostSize}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={1} sm={1} md={1.5} marginLeft={1} marginTop={3}>
                    <MuiCButton
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={() => addNewConstSizeEvent()}
                      sx={{
                        bgcolor: '#EE7B57',
                        '&:hover': {
                          bgcolor: '#D66A47',
                        },
                        height: '50px',
                        fontSize: '0.875rem',
                        textTransform: 'none',
                        borderRadius: '24px',
                      }}>
                      Add Space
                    </MuiCButton>
                  </Grid>
                </Grid>
                <Grid item justifyContent="start">
                  <Grid xs={12} sm={11}>
                    <ConstSizeList items={eventCostSizeList} onDelete={onDeleteSizeCost} />
                  </Grid>
                </Grid>
              </Dropdown>
            </Grid>
            <Grid item sm={12} paddingLeft={1}>
              <br />
              <br />
              <div>
                <hr
                  style={{
                    background: '#D9D9D9',
                    height: '2px',
                    border: 'none',
                  }}
                />
              </div>
              <div style={{ display: 'block', justifyContent: 'start', marginTop: 45 }}>
                <Checkbox
                  name="unlisted"
                  checked={values.unlisted}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
                <label>
                  {' '}
                  <b>Create Event as Unlisted</b>{' '}
                </label>
                <div>
                  <Typography style={{ marginLeft: 45 }}>
                    If this checkbox is selected, this event will not appear in the vendor
                    dashboard. You will need to share the link to this event directly with vendors
                    so they can apply.
                  </Typography>
                </div>
              </div>
            </Grid>
            <Grid container justifyContent="center" marginTop={3} marginBottom={8}>
              <Grid item xs={11} sm={5}>
                {canCreateEvents() && (
                  <Button type="submit" label="Create Event" disabled={isSubmitting} />
                )}
              </Grid>
            </Grid>
            <RecurrentEventModal
              isOpen={openRecurrentEventModal}
              startEventDate={values.event_date}
              setStartEventDate={setFieldValue}
              repeatEvery={repeatEvery}
              repeatEveryList={repeatEveryList}
              onChangeRepeatEvery={setRepeatEveryString}
              handleClose={onCloseRecurrentEventModal}
              onSave={onSaveRecurrentEventData}
            />
            {!canCreateEvents() && (
              <EventAlert
                isApproved={currentVenue?.status === VENUE_STATUS.APPROVED}
                isStripeConnected={user?.stripe_details?.details_submitted}
              />
            )}
          </Grid>
          <Snackbar open={openSnack} autoHideDuration={5000} onClose={handleCloseSnack}>
            <Alert severity="warning">{snackMessage}</Alert>
          </Snackbar>
        </Form>
      )}
    </Formik>
  );
}

export default function AddEvent({ onSaveEvent }) {
  return (
    <Grid container marginTop={5} justifyContent="center">
      <TranslucentContainer>
        <FormContentEvent onSaveEvent={onSaveEvent} />
      </TranslucentContainer>
    </Grid>
  );
}

FormContentEvent.propTypes = {
  onSaveEvent: PropTypes.func.isRequired,
};

AddEvent.propTypes = {
  onSaveEvent: PropTypes.func.isRequired,
};
